/* Ring builder engine — Sapphire Row
   Vanilla ES + custom elements, matching Edge's house style (no framework in the
   theme). Reads pre-built catalog assets; talks to nothing but /cart/add.js.

   STATE lives in the URL, not in localStorage. Every step is a real, shareable,
   crawlable address and browser back/forward just works. sessionStorage carries
   only the scroll position and the last-seen filter set, which are conveniences,
   never the build itself — a build that only exists in localStorage cannot be
   emailed to a partner, which is the single most common thing an engagement-ring
   shopper does.

   THE PRICE THE CUSTOMER SEES IS ALWAYS A REAL VARIANT PRICE. Nothing here
   computes a price to charge; it reads variant prices out of the pre-built map
   and adds them. /cart/add.js has no price field, so a tampered client cannot
   change what is charged. */
(function () {
  'use strict';

  var RB = (window.RingBuilder = window.RingBuilder || {});

  /* ---------------------------------------------------------------- utils */
  var money = function (cents) {
    // Edge's money format is decimal-free ($235). Prices arrive as float dollars.
    return '$' + Math.round(cents).toLocaleString('en-US');
  };
  RB.money = money;

  var el = function (tag, attrs, kids) {
    var n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null && attrs[k] !== false) n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  };
  RB.el = el;

  /* -------------------------------------------------------------- the build */
  // The whole build is these values. Everything else is derived.
  // `kind` (owner decision 2026-07-28) is what makes ONE builder serve rings and
  // pendants: it rides in the URL like every other field, so a pendant build is
  // just as shareable and back-button-safe as a ring one. /pages/engagement-ring-builder
  // stays alive as ?kind=ring so the existing URL keeps its SEO.
  var FIELDS = ['kind', 'style', 'shape', 'seat', 'metal', 'accent', 'stone', 'size', 'origin'];

  // Rings are the default so every pre-existing link, share and crawl keeps
  // working untouched — a missing ?kind must never mean "no settings".
  // `mount` is the word for the metal half. The pendant flow's own page copy says
  // "Start with a mounting" and the stone product page's button says "Choose a
  // Mounting", while the step bar said "Choose a setting" for the same click —
  // three names for one thing inside two screens of each other.
  //
  // `pair` (owner decision 2026-08-02) is the earring wave's one structural
  // difference and it is deliberately a NUMBER, not a boolean: it is the quantity
  // of BOTH halves that one build orders. Stuller sells an earring mounting as ONE
  // earring — 865 of 945 semi-mount SKUs are `UnitOfSale: Each` — so a finished
  // pair is two mountings AND two stones, where a ring is one and one. Everything
  // that counts (the cart, the total, the copy) multiplies by this, so a fourth
  // wave that happened to sell singly needs no new branch.
  //
  // 🚨 A PAIR IS ONLY POSSIBLE FROM CALIBRATED STOCK. Every serialized stone in
  // this catalogue is one of one — that is the whole premise of the /v2/gem feed —
  // so "order another one" does not exist for them. Calibrated stock is cut to a
  // standard millimetre and graded, so qty 2 of one SKU is consistent by
  // construction. The earring stone step is filtered on exactly that; see
  // `RB.pairable`.
  RB.KINDS = {
    ring:    { label: 'ring',    piece: 'Ring',    mount: 'setting',  sized: true,  pair: 1 },
    pendant: { label: 'pendant', piece: 'Pendant', mount: 'mounting', sized: false, pair: 1 },
    earring: { label: 'earrings', piece: 'Earring', mount: 'mounting', sized: false, pair: 2 }
  };

  // The wave the CURRENT PAGE declares (data-rb-kind), or null off-builder.
  // Read live rather than cached: pageReview and friends can run before and after
  // an async resolve, and a stale value would send step links to the wrong flow.
  RB.pageKind = function () {
    var el = document.querySelector('.rb[data-rb-kind]');
    var k = el && el.getAttribute('data-rb-kind');
    return RB.KINDS[k] ? k : null;
  };

  // PRECEDENCE: explicit ?kind= > the page's own declaration > ring.
  // The query param stays first so every pre-split link keeps its meaning after the
  // 301s (…/engagement-ring-builder?kind=pendant lands on the ring URL but is still
  // a pendant build). New links carry no ?kind= at all — the URL says which flow it is.
  RB.kind = function (build) {
    var q = new URLSearchParams(location.search).get('kind');
    var k = (build && build.kind) || q || RB.pageKind();
    return RB.KINDS[k] ? k : 'ring';
  };

  // A pendant has no finger size. Anything that asks for one must ask this first,
  // or the review step will block a pendant build on a size it can never have.
  RB.isSized = function (build) { return RB.KINDS[RB.kind(build)].sized; };

  // BOTH flows share ONE page template, so the hero <h1> and subheading are
  // section settings rendered by Liquid BEFORE `kind` is known — a pendant build
  // read "Design your ring" and "Here is your ring from every angle" while its
  // own step 3 correctly said "Your pendant". Liquid cannot fix this (kind is a
  // client-side URL param), so the piece word is swapped here, the same way the
  // step bar already does it. Scoped to the phrase "your <piece>" so nothing
  // touches "engagement ring" or a style name that legitimately says ring.
  RB.pieceCopy = function (text, build) {
    var label = RB.KINDS[RB.kind(build)].label;
    if (label === 'ring') return text;
    return String(text).replace(/\byour ring\b/gi, function (m) {
      return m.charAt(0) === 'Y' ? 'Your ' + label : 'your ' + label;
    });
  };

  // Rewrite the static hero copy in place. Only nodes the builder owns.
  RB.applyPieceCopy = function (root, build) {
    if (RB.kind(build) === 'ring') return;
    var sel = 'h1, .rb-hero__sub, [data-rb-subheading]';
    (root || document).querySelectorAll(sel).forEach(function (n) {
      if (n.children.length) return;              // only leaf text nodes
      var out = RB.pieceCopy(n.textContent, build);
      if (out !== n.textContent) n.textContent = out;
    });
  };

  RB.read = function () {
    var q = new URLSearchParams(location.search), b = {};
    FIELDS.forEach(function (f) { if (q.get(f)) b[f] = q.get(f); });
    b.kind = RB.kind(b);
    return b;
  };

  RB.href = function (path, build, extra) {
    var q = new URLSearchParams();
    FIELDS.forEach(function (f) {
      // `kind` is no longer a query field — the PATH says which flow this is, and
      // every builder page declares its own wave. Emitting it too would put
      // ?kind=pendant on /pages/pendant-builder, and one stale copy of it on a
      // ring URL would silently drag the shopper into the wrong flow. `extra` can
      // still set it deliberately.
      if (f !== 'kind' && build[f]) q.set(f, build[f]);
    });
    Object.keys(extra || {}).forEach(function (k) {
      if (extra[k] === null) q.delete(k); else q.set(k, extra[k]);
    });
    var s = q.toString();
    return path + (s ? '?' + s : '');
  };

  RB.go = function (path, build, extra) { location.href = RB.href(path, build, extra); };

  // ONE URL SET PER WAVE (owner decision 2026-07-29). Each flow has its own five
  // pages, so a link never needs ?kind= to say what it is and no builder URL says
  // "engagement" any more. The PAGES themselves are separate; the section, the
  // templates' logic and this module are NOT duplicated — every page renders the
  // same `ring-builder` section and differs only by two settings (page + kind).
  RB.PAGES = {
    ring: {
      settings: '/pages/ring-builder',
      stones: '/pages/ring-gemstones',
      stonesLab: '/pages/ring-gemstones-lab',
      stone: '/pages/ring-gemstone',
      review: '/pages/your-ring'
    },
    pendant: {
      settings: '/pages/pendant-builder',
      stones: '/pages/pendant-gemstones',
      stonesLab: '/pages/pendant-gemstones-lab',
      stone: '/pages/pendant-gemstone',
      review: '/pages/your-pendant'
    },
    earring: {
      settings: '/pages/earring-builder',
      stones: '/pages/earring-gemstones',
      stonesLab: '/pages/earring-gemstones-lab',
      stone: '/pages/earring-gemstone',
      review: '/pages/your-earrings'
    }
  };

  RB.pagesFor = function (kind) {
    return RB.PAGES[RB.KINDS[kind] ? kind : 'ring'];
  };

  // `RB.pages` stays a plain-looking object so every existing `RB.pages.review`
  // call site is untouched — it just resolves against the CURRENT wave now. A
  // getter, not a snapshot, because kind is known only once the page has rendered.
  try {
    Object.defineProperty(RB, 'pages', {
      get: function () { return RB.pagesFor(RB.kind(RB.read())); },
      configurable: true
    });
  } catch (e) {                      // pathological old engine: ring URLs, still usable
    RB.pages = RB.PAGES.ring;
  }

  /* ------------------------------------------------------------- catalog */
  // Scoped to the current `kind` by default: the shard now carries BOTH waves, and
  // a shopper building a pendant must never be shown a ring mounting. Settings
  // built before the pendant wave have no `kind` at all, so a missing value reads
  // as 'ring' rather than as "matches nothing" — the difference between an
  // untouched ring builder and an empty one.
  RB.settings = function (kind) {
    var all = (window.RB_SETTINGS && window.RB_SETTINGS.settings) || [];
    if (kind === '*') return all;
    var want = kind || RB.kind(RB.read());
    return all.filter(function (s) { return (s.kind || 'ring') === want; });
  };
  // Looking a style up by id must NOT be kind-scoped — the review step resolves a
  // style from the URL before it has decided anything about kind, and scoping here
  // would make a valid pendant build look like a broken one.
  RB.setting = function (style) {
    return RB.settings('*').filter(function (s) { return String(s.style) === String(style); })[0];
  };

  // The finger sizes a style can actually be made in. Per STYLE, not global:
  // pull_ring_sizes.py measured 29 sizes on 46 of the 51 wave-1 styles, 21 on
  // four and 17 on one, so a shared 4-9 list would offer sizes Stuller would
  // reject. Where a style's own SKUs disagreed the pull already narrowed it to
  // the intersection, so anything in here is makeable in every variant.
  RB.ringSizes = function (style) {
    var m = window.RB_RING_SIZES;
    return (m && m[String(style)]) || null;
  };

  RB.sizeLabel = function (v) {
    var n = parseFloat(v);
    return isNaN(n) ? String(v) : (n % 1 === 0 ? n.toFixed(0) : String(n));
  };

  RB.seatKey = function (shape, l, w) { return shape + '|' + l + '|' + w; };

  RB.parseSeat = function (key) {
    var p = String(key).split('|');
    return { shape: p[0], l: parseFloat(p[1]), w: parseFloat(p[2]) };
  };

  RB.seatLabel = function (key) {
    var s = RB.parseSeat(key), t = s.shape.charAt(0) + s.shape.slice(1).toLowerCase();
    return s.l === s.w ? t + ' ' + s.l.toFixed(2) + ' mm'
                       : t + ' ' + s.l.toFixed(2) + ' × ' + s.w.toFixed(2) + ' mm';
  };

  // (style, seat, metal, accent) -> [variantId, price]. Lazy: only the detail and
  // review pages load rb-setting-variants.js.
  RB.settingVariant = function (b) {
    var map = window.RB_SETTING_VARIANTS;
    if (!map) return null;
    return map[[b.style, b.seat, b.metal, b.accent].join('|')] || null;
  };

  /* ---- lazy asset loading --------------------------------------------- */
  // The renders map and the 14,233-entry variant map are only needed once the
  // customer opens a setting or reaches review. Inlining them on the grid took
  // step 1 from 6 KB to 225 KB gzipped, so they load on demand instead.
  var assetCache = {};
  RB.loadAsset = function (file, globalName) {
    if (window[globalName]) return Promise.resolve(window[globalName]);
    if (assetCache[file]) return assetCache[file];
    assetCache[file] = new Promise(function (res) {
      var sc = document.createElement('script');
      sc.src = (window.RB_ASSET_BASE || '') + file;
      sc.onload = function () { res(window[globalName] || null); };
      sc.onerror = function () { res(null); };
      document.head.appendChild(sc);
    });
    return assetCache[file];
  };
  RB.needSettingDetail = function () {
    return Promise.all([
      RB.loadAsset('rb-setting-variants.js', 'RB_SETTING_VARIANTS'),
      RB.loadAsset('rb-setting-renders.js', 'RB_RENDERS')
    ]);
  };

  /* ---- stone shards -------------------------------------------------- */
  var shardCache = {};
  RB.loadShard = function (shape, origin) {
    var key = shape + '|' + origin;
    if (shardCache[key]) return shardCache[key];
    var meta = window.RB_STONES_META;
    var info = meta && meta.shards[key];
    if (!info) return (shardCache[key] = Promise.resolve(null));
    shardCache[key] = new Promise(function (res) {
      if (window[info.var]) return res(window[info.var]);
      var s = document.createElement('script');
      // CACHE-BUST WITH THE BUILD STAMP. Shards are injected at runtime, so they
      // never get Liquid's `asset_url` ?v= hash and the browser would otherwise
      // hold a stale copy forever — which after an image recut means every card
      // pointing at media that has been deleted. rb-stones-meta.js IS versioned
      // by Liquid, so its `built` value is always fresh and makes a sound key.
      s.src = (window.RB_ASSET_BASE || '') + info.file +
              '?v=' + encodeURIComponent((meta && meta.built) || '1');
      s.onload = function () { res(window[info.var] || null); };
      s.onerror = function () { res(null); };
      document.head.appendChild(s);
    });
    return shardCache[key];
  };

  // Columnar -> objects, decoding the shared dictionary.
  RB.rows = function (shard) {
    if (!shard) return [];
    var c = shard.cols, v = (window.RB_STONES_META || {}).vocab || {};
    var d = function (f, i) { var x = c[f] && c[f][i]; return x == null || x < 0 ? null : (v[f] || [])[x]; };
    var out = [];
    for (var i = 0; i < shard.n; i++) {
      out.push({
        vid: c.v[i], handle: c.h[i], price: c.p[i], title: c.t[i],
        carat: c.ct[i], l: c.L[i], w: c.W[i],
        species: d('sp', i), color: d('col', i), clarity: d('cl', i),
        cut: d('cg', i), lab: d('lab', i), cert: c.cn[i], certUrl: c.cu[i],
        wear: d('wear', i), video: c.vid[i], img: c.img[i],
        // `cal` is absent on a shard built before the earring wave; `!!undefined`
        // is false, which is the safe direction — see RB.pairable.
        calibrated: !!(c.cal && c.cal[i]),
        available: !!c.av[i], shape: shard.shape, origin: shard.origin
      });
    }
    return out;
  };

  /* ------------------------------------------------- can this be a PAIR? --- */
  // 🚨 THE ONLY HONEST ANSWER IS THE ONE THE SUPPLY CHAIN GIVES.
  // A serialized stone is one of one — that is the definition of Stuller's /v2/gem
  // feed, and it is why a sold stone LEAVES the feed rather than going to zero. You
  // cannot order a second. Calibrated stock is the opposite: cut to a standard
  // millimetre and sold to a colour/clarity BAND (Stuller grades this stock "Ghi",
  // "F-G", "Si2-Si3" — a range, not a stone), so qty 2 of one SKU is consistent by
  // construction and needs nobody to match anything by hand.
  //
  // ⚠️ That distinction is exactly the owner's condition for pairing diamonds —
  // "calibrated, close in color and clarity" — and it is ALREADY what calibrated
  // means. Do NOT extend this to two serialized stones that merely look alike:
  // trade-matched means colour-paired by eye, which this business does not do, and
  // CATALOG_STATE records that as a promise not to make.
  //
  // Reads the shard's `cal` column, which build_builder_assets.py writes from the
  // product's own `calibrated` tag. Absent (an older shard) reads as NOT pairable,
  // so a stale asset makes the earring grid empty rather than making it wrong.
  RB.pairable = function (stone) { return !!(stone && stone.calibrated); };

  /* -------------------------------------------------- resolve a stone --- */
  // The build carries only the stone's VARIANT ID (that is all the cart needs).
  // Everything shown about it — title, price, cert, spin video, durability — has
  // to be looked up in its shard, which is keyed by shape + origin. Both are in
  // the build, so this is one shard fetch, not a search across all twenty.
  //
  // 🚨 A SHAPE IN THE BUILD IS A HINT, NOT A KEY. It used to be treated as one —
  // `shape ? [[shape, origin]] : []` — so a single wrong guess ended the search and
  // the stone came back null. Both ways of being wrong are ordinary, not exotic:
  //   - `origin` is a separate URL field, so `?stone=…&shape=EMERALD` with no
  //     `origin` looks in EMERALD|nat for a LAB stone and misses;
  //   - `shape` is ALSO the setting's centre-stone shape, and step 1b writes its
  //     own into the URL, so a build that picked a Round mounting then looked its
  //     Emerald stone up in the ROUND shard.
  // A null here does not read as "lookup failed" anywhere downstream — it reads as
  // "no stone chosen", which is how the review page came to quote a total with a
  // $3,065 diamond missing from it. So the hint is tried FIRST and the remaining
  // shards are a fallback, in order: stated origin, then the other origin of the
  // same shape (one more fetch, same shape), then everything else.
  //
  // The full scan is up to 2 MB, which is why `stone-set-into.liquid` emits shape
  // AND origin — with both correct this stops at the first fetch and the tail is
  // never walked. It is paid only by a legacy link, a hand-edited URL, or a stone
  // that has left the shard entirely (sold), and paying it beats a silent null.
  RB.resolveStone = function (build) {
    if (!build.stone) return Promise.resolve(null);
    var meta = window.RB_STONES_META || { shards: {} };
    var tries = [];
    var push = function (shape, origin) {
      if (!shape || !meta.shards[shape + '|' + origin]) return;
      if (tries.some(function (t) { return t[0] === shape && t[1] === origin; })) return;
      tries.push([shape, origin]);
    };
    var stated = build.origin === 'lab' ? 'lab' : 'nat';
    push(build.shape, stated);
    push(build.shape, stated === 'lab' ? 'nat' : 'lab');
    Object.keys(meta.shards).forEach(function (k) {
      var p = k.split('|');
      push(p[0], p[1]);
    });
    var want = String(build.stone);
    var i = 0;
    function next() {
      if (i >= tries.length) return Promise.resolve(null);
      var t = tries[i++];
      return RB.loadShard(t[0], t[1]).then(function (shard) {
        var hit = RB.rows(shard).filter(function (r) { return String(r.vid) === want; })[0];
        return hit || next();
      });
    }
    return next();
  };

  /* ------------------------------------------------- seat follows stone --- */
  // THE SEAT IS DERIVED FROM THE STONE, NOT CHOSEN BEFORE IT.
  // The customer picks a seat in step 1b, long before they know which stone they
  // want. If they then change shape in step 2 — which the shape chips allow,
  // because a style offers many shapes — the seat stayed on the old shape and
  // the build went to the cart as, literally, an Emerald 10x7 mounting holding
  // an Asscher stone. That is an order Stuller cannot make. So the moment a
  // stone is chosen, snap the seat to the best seat of THAT stone's shape.
  RB.seatsByShape = function (style) {
    var s = RB.setting(style);
    if (!s) return {};
    var by = {};
    s.seats.forEach(function (k) { (by[RB.parseSeat(k).shape] = by[RB.parseSeat(k).shape] || []).push(k); });
    return by;
  };

  RB.bestSeat = function (style, stone) {
    if (!stone) return null;
    var cands = (RB.seatsByShape(style)[stone.shape] || []).filter(function (k) {
      return RB.fits(stone, k);
    });
    if (!cands.length) return null;
    // tightest fit wins: the seat whose length is closest to the stone's
    return cands.sort(function (a, b) {
      return Math.abs(RB.parseSeat(a).l - stone.l) - Math.abs(RB.parseSeat(b).l - stone.l);
    })[0];
  };

  // A stone is compatible with a STYLE if any seat of that style can hold it.
  RB.fitsStyle = function (style, stone) { return !!RB.bestSeat(style, stone); };

  /* --------------------------------------------------- compatibility ---- */
  // Tolerances mirror stuller_settings.TOLERANCE. Undersize is more forgiving
  // than oversize because a stone larger than the seat physically will not sit.
  var TOL = { under: 0.15, over: 0.08 };

  RB.fits = function (stone, seatKey) {
    var s = RB.parseSeat(seatKey);
    if (!stone.l || stone.shape !== s.shape) return false;
    var sw = stone.w || stone.l;
    return (stone.l - s.l) <= TOL.over && (s.l - stone.l) <= TOL.under &&
           (sw - s.w) <= TOL.over && (s.w - sw) <= TOL.under;
  };

  /* --------------------------------------------------------- 360 spin ---- */
  // Frame sequences are generated by stone_spin.py. A stone with no video (all
  // calibrated lab color, plus 2 serialized stones with no media at Stuller)
  // falls back to its still — the viewer must never render an empty box.
  RB.mountSpin = function (stage, frames, opts) {
    opts = opts || {};
    if (!frames || frames.length < 2) return null;
    var imgs = frames.map(function (src, i) {
      var im = el('img', { src: src, alt: '', draggable: 'false' });
      if (i === 0) im.className = 'on';
      stage.appendChild(im);
      return im;
    });
    var N = frames.length, cur = 0, dragging = false, x0 = 0, f0 = 0, touched = false;

    function show(i) {
      var n = ((i % N) + N) % N;
      if (n === cur) return;
      imgs[cur].classList.remove('on');
      imgs[n].classList.add('on');
      cur = n;
    }
    function touch() { if (!touched) { touched = true; stage.classList.add('is-touched'); } }

    stage.addEventListener('pointerdown', function (e) {
      dragging = true; x0 = e.clientX; f0 = cur; touch();
      stage.classList.add('is-drag'); stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      show(f0 - Math.round((e.clientX - x0) / (stage.clientWidth * 0.85) * N));
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) {
        dragging = false; stage.classList.remove('is-drag');
        if (e.pointerId != null && stage.hasPointerCapture(e.pointerId)) stage.releasePointerCapture(e.pointerId);
      });
    });
    // keyboard parity: the spin must be operable without a pointer
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('role', 'img');
    stage.setAttribute('aria-label', opts.label || 'Drag or use arrow keys to rotate');
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { touch(); show(cur + 1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { touch(); show(cur - 1); e.preventDefault(); }
    });
    return { show: show, frames: N };
  };

  /* ------------------------------------------------------------- 5 angles */
  // THE URL HAS TO NAME EVERY OBJECT TO DRAW. The old fixed template asked for
  // metal + `g_Center` only, so every halo / three-stone / accented setting was
  // drawn WITHOUT its melee — a plainer ring than the customer is buying, on the
  // last screen before Add to cart. The obj list is Stuller's own, harvested from
  // FullySetImages by pull_setting_renders.py.
  //
  // These names are NOT guessable and must never be hardcoded here: the accent on
  // a three-stone is `g_Side`, and `g_Accent` — the obvious guess — returns a
  // valid 200 JPEG identical to the centre-only render, so a wrong name fails
  // silently and looks fixed. Change the harvest, not this file.
  //
  // Falls back to the fixed template when there is no list, which is what lets
  // this ship before the regenerated shard does.
  // 🚨 SPACES MUST GO OUT AS %20, NEVER AS '+'. Four object names contain a space
  // (`g_Accent 1` … `g_Accent 4`). `%20` and a raw space both render correctly;
  // '+' returns a valid 200 JPEG with the accents SILENTLY MISSING. So the space is
  // encoded here explicitly rather than left to whatever touches the URL next —
  // anything form-encoding it (URLSearchParams, a query builder) would produce '+'
  // and quietly reintroduce this exact bug. Only ' ' is encoded: the names also
  // contain '/', which must stay literal.
  RB.renderUrl = function (das, metal, rendition, objs) {
    var R = window.RB_RENDERS;
    if (!R) return null;
    var tpl = (objs && objs.length && R.pattern)
      ? R.pattern.replace('{objs}', objs.map(function (o) {
          return '&obj=' + o.replace(/ /g, '%20');
        }).join(''))
      : R.urls;
    return tpl.replace('{das}', das)
              .replace('{recipe}', R.recipes[metal] || 'white')
              .replace('{rendition}', rendition || 'standard');
  };

  // THE LIST BELONGS TO THE DAS ID, NOT TO THE STYLE. One style spans several
  // series, and the same (style, seat) can be served by different das ids with
  // different objects — 27 of 51 styles do. So the seat's own `o` (an index into
  // R.objsets, written by the same harvest row that produced these das ids) is
  // authoritative; R.objs[style] is only a coarse fallback for a seat without one.
  RB.objsFor = function (style, seatKey) {
    var R = window.RB_RENDERS;
    if (!R) return null;
    var set = (R.renders[String(style)] || {})[seatKey];
    if (set && R.objsets && set.o != null && R.objsets[set.o]) return R.objsets[set.o];
    return (R.objs && R.objs[String(style)]) || null;
  };

  RB.anglesFor = function (style, seatKey, metal) {
    var R = window.RB_RENDERS;
    if (!R) return [];
    var byStyle = R.renders[String(style)] || {};
    var set = byStyle[seatKey];
    if (!set) return [];
    var objs = RB.objsFor(style, seatKey);
    return R.angles.filter(function (a) { return set[a]; }).map(function (a) {
      return { angle: a, url: RB.renderUrl(set[a], metal, 'zoom', objs) };
    });
  };

  /* ------------------------------------------------------------ add to cart */
  // TWO line items joined by a shared _build_id. Underscore-prefixed properties
  // are hidden by Edge's cart templates; the visible ones must be
  // SELF-DESCRIBING because the cart DRAWER renders values with the label
  // visually-hidden, so a bare "1 of 2" would read as nonsense there.
  RB.buildId = function () {
    return (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)).toUpperCase();
  };

  // How many of EACH half this build orders. 1 for a ring or a pendant, 2 for a
  // pair of earrings — see the `pair` note on RB.KINDS.
  RB.pairQty = function (build) { return RB.KINDS[RB.kind(build)].pair || 1; };

  RB.addToCart = function (build, opts) {
    var sv = RB.settingVariant(build);
    if (!sv) return Promise.reject(new Error('setting variant not found'));
    if (!opts.stoneVariantId) return Promise.reject(new Error('no stone chosen'));
    var qty = RB.pairQty(build);
    // 🚨 A PAIRED BUILD MUST NOT REACH THE CART FROM A SERIALIZED STONE. The grid
    // filters to calibrated stock, but this is the last gate before money, and a
    // shared or hand-edited URL bypasses every grid. Ordering qty 2 of a one-of-one
    // stone is an order Stuller cannot fill: it would confirm, then arrive short,
    // and the customer would have paid for two.
    if (qty > 1 && opts.stone && !RB.pairable(opts.stone)) {
      return Promise.reject(new Error('that gemstone is one of a kind, so it cannot be made as a pair'));
    }
    var id = RB.buildId();
    var shared = {
      '_build_id': id,
      '_rb_style': String(build.style),
      '_rb_seat': build.seat,
      '_rb_kind': RB.kind(build),
      // THE WHOLE BUILD, so the cart can offer "Change" on either half the way
      // Blue Nile does. Without it the cart knows the style and the seat but not
      // the metal, accent, stone or origin, and a "Change setting" link would
      // drop the shopper into a half-empty builder — the same partial-copy
      // mistake that made step 1b lose the stone. `RB.href('')` emits exactly
      // RB.FIELDS, so this stays correct when a field is added.
      '_rb_q': RB.href('', build).replace(/^\?/, ''),
      'Part of': 'Custom ' + RB.KINDS[RB.kind(build)].label + ' · build ' + id
    };
    // Only properties the VARIANT does not already state. Shopify prints the
    // variant's own options above these, so repeating Metal and the seat put
    // "Metal: 14K Yellow Gold" and the centre-stone size on screen twice — and
    // "Stone: <title>" restated the stone line's own title verbatim.
    // The component labels have to state the PAIR, because the cart drawer renders
    // a property's label visually-hidden and shows only its value. "Setting (1 of
    // 2)" beside a line of quantity 2 reads as a contradiction; "Both mountings"
    // reads correctly on its own, which is the standing rule for these strings.
    var half = qty > 1 ? ' (pair)' : '';
    // `_rb_part` is the STABLE machine-readable half-marker. The cart guard needs
    // to tell the metal half from the stone half, and `Component` is customer copy
    // that changes with the wave ("Setting" / "Both mountings") — matching on it
    // would break the guard the next time the wording is edited. Underscore
    // prefix, so Edge's cart templates never print it.
    var settingProps = Object.assign({}, shared, {
      '_rb_part': 'setting',
      'Component': qty > 1 ? 'Both mountings (1 of 2)' : 'Setting (1 of 2)'
    });
    // A pendant has no finger size, and neither does an earring. Sending
    // "Ring size: To be confirmed" on either would put a line on the packing slip
    // that Stuller cannot act on and the customer never chose.
    if (RB.isSized(build)) settingProps['Ring size'] = build.size || 'To be confirmed';
    var stoneProps = Object.assign({}, shared, {
      '_rb_part': 'stone',
      'Component': qty > 1 ? 'Both center stones (2 of 2)' : 'Center stone (2 of 2)'
    });
    // ⚠️ TWO LINE ITEMS AT QUANTITY 2, NOT FOUR LINE ITEMS. Shopify merges lines
    // with identical id AND identical properties, so posting the same variant
    // twice would collapse to one line of quantity 2 anyway — but only by
    // accident, and only while the properties happen to match. Ask for what we
    // mean. It also keeps the cart readable: a pair is two lines, like every
    // other build.
    if (half) { settingProps['Quantity'] = 'Pair'; stoneProps['Quantity'] = 'Pair'; }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          { id: sv[0], quantity: qty, properties: settingProps },
          { id: opts.stoneVariantId, quantity: qty, properties: stoneProps }
        ]
      })
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart');
        return j;
      });
    });
  };

  /* ------------------------------------------------- sort + filter drawer */
  // Shared by step 1 and step 2 so the two surfaces cannot drift apart. Both
  // benchmarks put a plain dropdown at the right of the results bar and a
  // Filters control at the left; on a phone both collapse into one sticky bar.
  RB.mountSort = function (wrap, options, current, onPick) {
    if (!wrap) return;
    wrap.innerHTML = '';
    var label = (options.filter(function (o) { return o.value === current; })[0] || options[0]).label;
    var btn = el('button', {
      class: 'rb-sort__btn', type: 'button', 'aria-expanded': 'false',
      text: 'Sort by: ' + label
    });
    var menu = el('div', { class: 'rb-sort__menu', hidden: 'hidden' });
    options.forEach(function (o) {
      menu.appendChild(el('button', {
        class: 'rb-sort__opt', type: 'button',
        'aria-selected': String(o.value === current),
        text: o.label,
        onclick: function () { onPick(o.value); }
      }));
    });
    btn.addEventListener('click', function () {
      var open = !menu.hidden;
      menu.hidden = open;
      btn.setAttribute('aria-expanded', String(!open));
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
    });
    wrap.appendChild(btn);
    wrap.appendChild(menu);
    return { close: function () { menu.hidden = true; } };
  };

  RB.mountFilterDrawer = function (root, rail, opts) {
    opts = opts || {};
    var fb = root.querySelector('[data-rb-filterbar]');
    if (!fb || !rail) return;
    fb.innerHTML = '';
    var openBtn = el('button', { type: 'button', 'aria-expanded': 'false' },
      [el('span', { text: 'Filters' })]);
    if (opts.activeCount) {
      openBtn.appendChild(el('span', { class: 'rb-fb-count', text: String(opts.activeCount) }));
    }
    var scrim = root.querySelector('[data-rb-scrim]');
    function setOpen(open) {
      rail.setAttribute('data-open', String(open));
      if (scrim) scrim.hidden = !open;
      openBtn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open && window.trapFocus) window.trapFocus(rail, rail.querySelector('button'));
      else if (window.removeTrapFocus) window.removeTrapFocus(openBtn);
    }
    openBtn.addEventListener('click', function () { setOpen(true); });
    if (scrim) scrim.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && rail.getAttribute('data-open') === 'true') setOpen(false);
    });
    fb.appendChild(openBtn);
    if (opts.sortLabel) {
      fb.appendChild(el('button', {
        type: 'button', text: opts.sortLabel,
        onclick: function () {
          var b = root.querySelector('.rb-sort__btn');
          if (b) { b.scrollIntoView({ block: 'nearest' }); b.click(); }
        }
      }));
    }
    if (!rail.querySelector('.rb-rail__head')) {
      // the live count belongs INSIDE the drawer too — the shopper is changing
      // filters here and cannot see the count on the page behind the scrim
      if (opts.countLabel) {
        rail.insertBefore(el('div', { class: 'rb-rail__count', text: opts.countLabel }),
                          rail.firstChild);
      }
      rail.insertBefore(el('div', { class: 'rb-rail__head' }, [
        el('strong', { text: 'Filters' }),
        el('button', { class: 'rb-rail__close', type: 'button', 'aria-label': 'Close filters',
          text: '✕', onclick: function () { setOpen(false); } })
      ]), rail.firstChild);
      rail.appendChild(el('div', { class: 'rb-rail__apply' }, [
        el('button', { class: 'btn btn-primary', type: 'button',
          text: opts.applyLabel || 'Show results',
          onclick: function () { setOpen(false); } })
      ]));
    }
    return { setOpen: setOpen };
  };

  /* ---------------------------------------------------------- step bar --- */
  RB.stepBar = function (mount, build, active) {
    var s = build.style ? RB.setting(build.style) : null;
    var sv = RB.settingVariant(build);
    var kind = RB.KINDS[RB.kind(build)];
    // Every price in this bar is a per-UNIT variant price, and a pair orders two
    // of each half — see the same multiplication on the review page. Without it
    // the bar quotes half of what the cart charges. 1 for rings and pendants.
    var q = kind.pair || 1;
    var steps = [
      {
        n: 1, label: 'Choose a ' + kind.mount, href: RB.pages.settings,
        value: s ? s.title.replace(/ Setting$/, '') : 'Browse ' + kind.mount + 's',
        price: sv ? sv[1] * q : (s ? s.min * q : null),
        // The exact price needs rb-setting-variants.js, and that 14,233-entry
        // map is deliberately NOT loaded on steps 1-2b (it is the difference
        // between a 6 KB and a 225 KB first load). So outside review we only
        // have the style's MINIMUM, which for a configured setting can sit well
        // under what it will actually cost — $865 against $975 on style 222142.
        // Say "from" rather than quietly quoting the cheapest variant.
        from: !sv && !!s,
        done: !!build.style, img: s && s.img
      },
      {
        n: 2, label: 'Choose a gemstone',
        href: build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
        value: build.stone ? (window.RB_STONE_TITLE || 'Selected') : 'Browse gemstones',
        price: window.RB_STONE_PRICE ? window.RB_STONE_PRICE * q : null, done: !!build.stone,
        disabled: !build.style
      },
      {
        n: 3, label: 'Your ' + kind.label, href: RB.pages.review,
        value: build.stone && build.style ? 'Review and add to cart' : 'Complete both steps',
        price: (sv && window.RB_STONE_PRICE) ? (sv[1] + window.RB_STONE_PRICE) * q : null,
        disabled: !(build.style && build.stone)
      }
    ];
    var bar = el('nav', { class: 'rb-steps',
      'aria-label': kind.piece + ' builder progress' });
    steps.forEach(function (st) {
      var a = el('a', {
        class: 'rb-step' + (st.done ? ' is-done' : ''),
        href: st.disabled ? null : RB.href(st.href, build),
        'aria-current': st.n === active ? 'step' : null,
        'aria-disabled': st.disabled ? 'true' : null
      }, [
        el('span', { class: 'rb-step__n', text: st.done && st.n !== active ? '✓' : String(st.n) }),
        st.img ? el('img', { class: 'rb-step__thumb', src: st.img, alt: '', loading: 'lazy' }) : null,
        el('span', { class: 'rb-step__body' }, [
          el('span', { class: 'rb-step__label', text: st.label }),
          el('span', { class: 'rb-step__value',
            text: st.value + (st.price ? ' · ' + (st.from ? 'from ' : '') + money(st.price) : '') })
        ]),
        st.done && st.n !== active ? el('span', { class: 'rb-step__edit', text: 'Change' }) : null
      ]);
      bar.appendChild(a);
    });
    mount.innerHTML = '';
    mount.appendChild(bar);
  };
})();
